import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { setLanguage } from "@/i18n";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("sp_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      localStorage.setItem("sp_user", JSON.stringify(r.data));
      return r.data;
    } catch {
      setUser(null);
      localStorage.removeItem("sp_user");
      localStorage.removeItem("sp_token");
      return null;
    }
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((u) => {
      const next = { ...(u || {}), ...patch };
      localStorage.setItem("sp_user", JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    // Allinea la lingua dell'interfaccia alla preferenza salvata sul profilo.
    if (user?.language) setLanguage(user.language);
  }, [user?.language]);

  useEffect(() => {
    // Skip /auth/me if we are returning from Emergent OAuth callback —
    // AuthCallback will handle it and refresh explicitly.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.otp_required) {
      return { otp_required: true, email: data.email, message: data.message };
    }
    localStorage.setItem("sp_token", data.access_token);
    localStorage.setItem("sp_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const verifyLoginOtp = async (email, code) => {
    const { data } = await api.post("/auth/login/verify", { email, code });
    localStorage.setItem("sp_token", data.access_token);
    localStorage.setItem("sp_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.otp_required) {
      return { otp_required: true, email: data.email, message: data.message };
    }
    localStorage.setItem("sp_token", data.access_token);
    localStorage.setItem("sp_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const verifyRegisterOtp = async (email, code) => {
    const { data } = await api.post("/auth/register/verify", { email, code });
    localStorage.setItem("sp_token", data.access_token);
    localStorage.setItem("sp_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("sp_token");
    localStorage.removeItem("sp_user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, loginWithGoogle, refresh, updateUser, verifyLoginOtp, verifyRegisterOtp }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
